/** @file applyBundleCells: each pick's sheet rect read from the bundle's resolved entry (rects per tile). */
import type { CellRect, ExportPuppet, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import type { FacetPick } from '@zilo/pose3d-facet/contract';

/** The resolved entry and rect of a pick, or null when the drawable, entry or rect is absent. */
function cellOf(puppet: ExportPuppet, pick: FacetPick): { entry: ResolvedEntry; rect: CellRect } | null {
  const drawable = puppet.drawables.find((d) => d.id === pick.drawable);
  const entry = drawable?.kind === 'impostor' ? drawable.facets[pick.entry] : undefined;
  const rect = entry?.rects[pick.tile];
  return entry === undefined || rect === undefined || rect === null ? null : { entry, rect };
}

/**
 * For every pick that is neither hidden nor without an entry: `rect = drawable.facets[pick.entry].rects[pick.tile]`
 * (the drawable matched by id) and `sheet` = the entry's sheet; a null rect makes the pick `missing` with rect
 * `[0, 0, 0, 0]`. Variants and frame keys are entries (R1), so the pick's entry already encodes them.
 */
export function applyBundleCells(puppet: ExportPuppet, picks: FacetPick[]): void {
  for (const pick of picks) {
    if (pick.hidden || pick.entry < 0 || pick.tile < 0) continue;
    const cell = cellOf(puppet, pick);
    const rect = cell?.rect ?? [0, 0, 0, 0];
    pick.missing = cell === null;
    if (cell !== null) pick.sheet = cell.entry.sheet;
    pick.rect[0] = rect[0]; pick.rect[1] = rect[1]; pick.rect[2] = rect[2]; pick.rect[3] = rect[3];
  }
}
